import assert from "assert";
import { NarrowedInterpretable } from "./interpretable";

export abstract class Representation<RepresentationType> {
    /**
     * Initialize this Language so it's ready to translate back and forth. Must be awaited by
     * any part of the program wanting to use this representation.
     * May fail with an exception.
     */
    public abstract initialize(): Promise<void>;

    /**
     * Converts a RepresentationType representing either all or part of a program into an Interpretable.
     * @param representation the underlying representation.
     * @returns a Map of interpretables
     */
    abstract fromRepresentation(representation: RepresentationType): Map<string, NarrowedInterpretable>;

    /**
     * Converts a Interpretable to a RepresentationType
     * @param interpretable a program representing part or all of the syntax tree.
     */
    abstract toRepresentation(interpretable: NarrowedInterpretable): RepresentationType;
}

/**
 * An ASTChangeListener is a callback function taking in a new rootInterpretable.
 */
export type ASTChangeListener = (interpretableMap: Map<string, NarrowedInterpretable>) => void;

/**
 * A Visualizer is meant to encompass code editors. So the flowchart editor and the CodeMirror editor.
 * These are user interactable and allow the user to make changes, which the program should listen to.
 * 
 * Generally, Visualizers should use a Representation to convert back and forth.
 */
export abstract class Visualizer {

    /**
     * All listeners to notify.
     */
    private listeners: ASTChangeListener[] = [];
    
    /**
     * Listen for any changes this listener submits to the AST.
     * @param listener A function taking an Interpretable as its argument.
     */
    public onASTChange(listener: ASTChangeListener) {
        this.listeners.push(listener);
    }
    
    /**
     * Stop listening for changes on the AST.
     * @param listener A listener. Will throw an error if it wasn't added with onASTChange beforehand.
     */
    public unregisterASTChange(listener: ASTChangeListener) {
        const index = this.listeners.indexOf(listener);
        assert(index != -1, `You tried to unregister an ASTChangeListener but it isn't in the list.`)
        this.listeners.splice(index, 1);
    }

    /**
     * Submit a change to the AST
     * @param interpretableMap a map of all interpretables with their UUID
     */
    protected pushASTChange(interpretableMap: Map<string, NarrowedInterpretable>) {
        for(const listener of this.listeners) {
            listener(interpretableMap);
        }
    }
    
    protected pushPartialASTChange(childUUID: string, replaceWithUUID: string) {
        
    }
}